import React from "react";
import styled from "styled-components";
import { MdRemoveCircleOutline, MdAddCircleOutline } from "react-icons/md";
import formatCurrency from "src/lib/format-currency";
import { GIFT_WRAPPING_COST_IN_PENCE } from "src/data/app-constants";
import {
  GIFTSET_SKU,
  MINI_GIFTSET_SKU,
  LUXURY_WASHSET_SKU,
  LUXGIFTSETGOLD_SKU,
} from "src/data/product-skus";
import RazorEngraving from "src/react/components/razor-engraving";
import { color } from "src/styles/variables";
import { Checkbox, TextAreaEl } from "src/react/forms";
import getProductOfferDiscountedPrice from "../helpers/get-product-offer-discounted-price";

function GiftsBasketItem({
  isGiftOffer,
  product,
  productData,
  quantity,
  addProduct,
  editProduct,
  removeProduct,
}) {
  const isGiftSet =
    product.id === GIFTSET_SKU ||
    product.id === MINI_GIFTSET_SKU ||
    product.id === LUXURY_WASHSET_SKU ||
    product.id === LUXGIFTSETGOLD_SKU;
  const hasHandle =
    product.id === GIFTSET_SKU ||
    product.id === MINI_GIFTSET_SKU ||
    product.id === LUXGIFTSETGOLD_SKU;
  const productPrice = isGiftOffer
    ? getProductOfferDiscountedPrice(productData.price)
    : productData.price;

  return (
    <GiftsBasketItemWrapper data-selector={`GiftsBasketItem-${product.id}`}>
      <FlexWrapper>
        <ImageWrapper>
          <img src={productData.image} alt={productData.name} />
        </ImageWrapper>
        <ContentWrapper>
          <Title>
            {productData.name} <span>- £{productPrice} each</span>
          </Title>
          <Controls>
            <ProductButton
              data-selector={`GiftsBasketItem-${product.id}-remove`}
              style={{ cursor: "pointer" }}
              onClick={removeProduct}
            >
              <MdRemoveCircleOutline size={35} />
            </ProductButton>
            <Quantity data-selector="GiftsBasketItem-Quantity">
              {quantity}
            </Quantity>
            <ProductButton
              data-selector={`GiftsBasketItem-${product.id}-add`}
              style={{ cursor: "pointer" }}
              onClick={addProduct}
            >
              <MdAddCircleOutline size={35} style={{ cursor: "pointer" }} />
            </ProductButton>
          </Controls>
          <SubTotal>Subtotal: £{quantity * productPrice}</SubTotal>
        </ContentWrapper>
      </FlexWrapper>
      {hasHandle && (
        <OptionsWrapper>
          <BoxOptionsTitle>Personalised initials</BoxOptionsTitle>
          <BoxOptionsCopy>
            If you'd like, we can personalise your razor handle for you with up
            to 3 initials. There's no additional charge for this, it's on us.
          </BoxOptionsCopy>
          {product.options.map((option, index) => {
            const engraving = option.engraved_initials;
            const updateEngraving = (engraving) => {
              editProduct({
                product,
                itemIndex: index,
                itemOptions: { engraved_initials: engraving },
              });
            };
            return (
              <BoxOptionsWrapper
                key={index}
                data-selector="GiftsBasketItem-PersonalisedInitials"
              >
                <RazorEngraving
                  gold={product.id === LUXGIFTSETGOLD_SKU}
                  engraving={engraving}
                  updateEngraving={updateEngraving}
                >
                  <BoxNumber>Box {index + 1}</BoxNumber>
                </RazorEngraving>
              </BoxOptionsWrapper>
            );
          })}
        </OptionsWrapper>
      )}
      <OptionsWrapper>
        <BoxOptionsTitle>Gift Wrap & Message</BoxOptionsTitle>
        {product.options.map((option, i) => {
          return (
            <BoxOptionsWrapper key={`BoxOptions-${i}`}>
              <Checkbox
                id={"giftWrapping" + product.id + i}
                checked={option.gift_wrapping}
                onChange={() => {
                  editProduct({
                    product,
                    itemIndex: i,
                    itemOptions: {
                      ...option,
                      gift_wrapping: !option.gift_wrapping,
                      gift_message: "",
                    },
                  });
                }}
              >
                {isGiftSet ? "Box" : "Bag"} {i + 1}, gift wrap & message (+
                {formatCurrency({ value: GIFT_WRAPPING_COST_IN_PENCE })})
              </Checkbox>

              {option.gift_wrapping && (
                <GiftMessageWrapper>
                  <TextAreaEl
                    maxLength="120"
                    defaultValue={option.gift_message}
                    placeholder="Add your message here, maximum 120 characters."
                    onBlur={(event) => {
                      editProduct({
                        product,
                        itemIndex: i,
                        itemOptions: {
                          ...option,
                          gift_message: event.target.value,
                        },
                      });
                    }}
                  />
                </GiftMessageWrapper>
              )}
            </BoxOptionsWrapper>
          );
        })}
      </OptionsWrapper>
    </GiftsBasketItemWrapper>
  );
}

export default GiftsBasketItem;

const GiftsBasketItemWrapper = styled.div`
  margin-bottom: 40px;
  :not(:last-child) {
    border-bottom: 1px solid ${color.midGrey};
  }
`;

const FlexWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 600px;
  margin: 0 auto 40px;
`;

const ImageWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-basis: 40%;
  margin-right: 20px;
  @media (min-width: 650px) {
    & img {
      height: 200px;
    }
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: 60%;
`;

const Title = styled.h2`
  text-transform: uppercase;
  font-style: normal;
  color: ${color.grey};
  font-weight: 400;
  margin-bottom: 10px;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const Quantity = styled.div`
  width: 40px;
  text-align: center;
  border-bottom: 1px solid;
  margin: 0 10px;
`;

const SubTotal = styled.h4`
  text-transform: uppercase;
  font-style: normal;
  color: ${color.grey};
  font-weight: 400;
  margin-bottom: 10px;
`;

const OptionsWrapper = styled.div`
  display: flex;
  justify-content: center;
  max-width: 600px;
  margin: 0 auto 40px;
  flex-direction: column;
`;

const BoxOptionsWrapper = styled.div`
  padding: 10px 0;
  margin-bottom: 10px;
`;

const BoxOptionsTitle = styled.div`
  text-transform: uppercase;
  font-style: normal;
  color: ${color.grey};
  font-weight: 400;
  margin-bottom: 10px;
`;

const BoxOptionsCopy = styled.p`
  font-size: 14px;
  line-height: 1.45em;
`;

const GiftMessageWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: baseline;
  margin-top: 20px;
`;

const ProductButton = styled.button`
  border: none;
  background: none;
`;

const BoxNumber = styled.div`
  font-weight: 400;
  margin-right: 20px;
`;
